import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';   // ✅ ADD THIS
import App from './App';


// ✅ GLOBAL AXIOS INTERCEPTOR (VERY IMPORTANT)
axios.interceptors.request.use((config) => {
  // Skip Authorization header for these endpoints to avoid CORS preflight
  const skippedEndpoints = [
    "/api/interviews/log-start",
    "/api/assessments/context/",
    "/api/interviews/ai-interview/more-questions",
    "/api/interviews/ai-interview/submit/",
    "/api/interviews/ai-interview/terminate/"
  ];

  if (config.url && skippedEndpoints.some(endpoint => config.url.includes(endpoint))) {
    return config;
  }

  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});


// Create custom theme with optimized SaaS aesthetics
const theme = createTheme({
  palette: {
    primary: { 
      main: '#4F46E5', // Accent Text / Primary Accent
      light: '#60A5FA', // Hover / Accent Light
      dark: '#4338CA', // Accent Hover
      contrastText: '#FFFFFF',
    },
    secondary: { 
      main: '#1E3A8A', // Links / Secondary Indigo
    },
    background: { 
      default: '#F8FAFC', 
      paper: '#FFFFFF' 
    },
    text: { 
      primary: '#0A0F2C', // Primary Text (Dark Navy)
      secondary: '#475569', // Secondary Text
      disabled: '#64748B', // Muted Text
    },
    success: { main: '#4F46E5' },
    warning: { main: '#F59E0B' },
    error: { main: '#EF4444' },
    divider: '#E5E7EB',
  },
  typography: {
    fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
    h1: { fontWeight: 800, fontSize: '56px', color: '#0A0F2C' },
    h2: { fontWeight: 800, fontSize: '48px', color: '#0A0F2C' },
    h3: { fontWeight: 800, fontSize: '40px', color: '#0A0F2C' }, // Dashboard Title
    h4: { fontWeight: 700, fontSize: '32px', color: '#0A0F2C' },
    h5: { fontWeight: 700, fontSize: '24px', color: '#0A0F2C' },
    h6: { fontWeight: 600, fontSize: '18px', color: '#0A0F2C' },
    subtitle1: { fontWeight: 500, fontSize: '14px', color: '#475569' }, // Card Labels
    body1: { color: '#0A0F2C' },
    body2: { fontSize: '13px', color: '#475569' }, // Analytics Labels
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 20,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(79,70,229,0.15)',
          },
          '&.Mui-disabled': {
            backgroundColor: '#CBD5E1 !important',
            color: '#64748B !important',
            borderColor: '#CBD5E1 !important',
            cursor: 'not-allowed !important',
            boxShadow: 'none !important',
            opacity: '0.7 !important', // Slightly more reduction
            pointerEvents: 'auto !important', // Enable pointer events to show not-allowed cursor
            '&:hover': {
              backgroundColor: '#CBD5E1 !important',
              boxShadow: 'none !important',
            },
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #4338CA 0%, #3730A3 100%)',
          },
          '&.Mui-disabled': {
            background: '#CBD5E1 !important',
          },
        },
        outlinedPrimary: {
          borderColor: '#4F46E5',
          color: '#4F46E5',
          '&:hover': {
            backgroundColor: 'rgba(79, 70, 229, 0.04)',
            borderColor: '#4338CA',
          },
          '&.Mui-disabled': {
            borderColor: '#CBD5E1 !important',
          },
        },
        containedSecondary: {
          backgroundColor: '#1E3A8A',
          '&:hover': {
            backgroundColor: '#172554',
          },
          '&.Mui-disabled': {
            backgroundColor: '#CBD5E1 !important',
          },
        },
        outlinedSecondary: {
          borderColor: '#1E3A8A',
          color: '#1E3A8A',
          '&:hover': {
            backgroundColor: 'rgba(30, 58, 138, 0.04)',
            borderColor: '#172554',
          },
          '&.Mui-disabled': {
            borderColor: '#CBD5E1 !important',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 20px rgba(10,15,44,0.05)',
          transition: '200ms ease-out',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: '#1E3A8A',
          fontWeight: 600,
          '&:hover': { color: '#60A5FA' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#475569',
          '&.Mui-focused': { color: '#4F46E5' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          '::placeholder': { color: '#64748B', opacity: 1 },
        },
      },
    }
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </React.StrictMode>
);
